import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../../lib/utils";

/**
 * Tooltip Provider - Wraps application to enable tooltips
 */
const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Tooltip Root - Contains tooltip trigger and content
 */
const Tooltip = TooltipPrimitive.Root;

/**
 * Tooltip Trigger - Element that triggers the tooltip
 */
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * SVG Arrow with border - matches Figma design
 * Creates a triangle with stroke (border) and fill
 */
const TooltipArrowSvg: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="20"
    height="10"
    viewBox="0 0 20 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Arrow shape with border */}
    <path
      d="M10 10L0 0H20L10 10Z"
      fill="white"
      stroke="#d4d4d8"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* White fill to cover the top border line */}
    <rect x="0" y="0" width="20" height="2" fill="white" />
  </svg>
);

export interface TooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  /** Whether to show the arrow. Default: true */
  showArrow?: boolean;
  /** Custom arrow class name */
  arrowClassName?: string;
}

/**
 * Tooltip Content - The content displayed in the tooltip
 *
 * Based on Capsule Design System tooltip component.
 * Supports multiple positions (top, bottom, left, right) and alignments (start, center, end).
 *
 * Position mapping to Figma:
 * - side="left" (Start position) - tooltip appears to the left of trigger, arrow points right
 * - side="right" (End position) - tooltip appears to the right of trigger, arrow points left
 * - side="top" (Top position) - tooltip appears above trigger, arrow points down
 * - side="bottom" (Bottom position) - tooltip appears below trigger, arrow points up
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger>Hover me</TooltipTrigger>
 *     <TooltipContent>
 *       Tooltip message here
 *     </TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 * ```
 *
 * @example
 * ```tsx
 * // With different positions
 * <TooltipContent side="bottom" align="start">
 *   Bottom-left aligned tooltip
 * </TooltipContent>
 *
 * // Without arrow
 * <TooltipContent showArrow={false}>
 *   Tooltip without arrow
 * </TooltipContent>
 * ```
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      sideOffset = 10,
      showArrow = true,
      arrowClassName,
      children,
      side = "top",
      align = "center",
      ...props
    },
    ref,
  ) => {
    // Get arrow position and rotation based on tooltip side and alignment
    const getArrowStyles = (): { position: string; rotation: string } => {
      // SVG arrow points DOWN by default (tip at bottom)
      // Rotation to point TOWARD the trigger:
      const rotations: Record<string, string> = {
        top: "", // Arrow at bottom of tooltip, pointing down toward trigger (no rotation)
        bottom: "rotate-180", // Arrow at top of tooltip, pointing up toward trigger
        left: "-rotate-90", // Arrow at right of tooltip, pointing right toward trigger
        right: "rotate-90", // Arrow at left of tooltip, pointing left toward trigger
      };

      // Arrow position based on side and alignment
      // SVG dimensions: 20x10 (after 90deg rotation: 10x20)
      const positions: Record<string, Record<string, string>> = {
        top: {
          start: "-bottom-[8px] left-4",
          center: "-bottom-[8px] left-1/2 -translate-x-1/2",
          end: "-bottom-[8px] right-4",
        },
        bottom: {
          start: "-top-[8px] left-4",
          center: "-top-[8px] left-1/2 -translate-x-1/2",
          end: "-top-[8px] right-4",
        },
        left: {
          start: "-right-[8px] top-2",
          center: "-right-[8px] top-1/2 -translate-y-1/2",
          end: "-right-[8px] bottom-2",
        },
        right: {
          start: "-left-[8px] top-2",
          center: "-left-[8px] top-1/2 -translate-y-1/2",
          end: "-left-[8px] bottom-2",
        },
      };

      return {
        position: positions[side]?.[align] || positions.top.center,
        rotation: rotations[side] || "",
      };
    };

    const arrowStyles = getArrowStyles();

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            // Base styles matching Figma design
            "relative z-50 max-w-[280px] rounded-lg border border-border-secondary bg-bg-primary px-4 py-3",
            // Shadow: 0px 4px 8px rgba(0,0,0,0.12)
            "shadow-[0px_4px_8px_rgba(0,0,0,0.12)]",
            // Text styling
            "text-sm leading-5 text-text-secondary",
            // Animation
            "animate-in fade-in-0 zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2",
            "data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2",
            "data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...props}
        >
          {children}
          {showArrow && (
            <div
              className={cn("absolute", arrowStyles.position, arrowClassName)}
            >
              <TooltipArrowSvg className={arrowStyles.rotation} />
            </div>
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
